import { supabase } from '../../lib/supabaseClient';

export type Album = {
  id: string;
  title_en: string;
  title_th: string | null;
  slug: string;
  release_date: string | null;
  cover_storage_path: string | null;
  is_single: boolean;
  is_featured: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
};

export type Track = {
  id: string;
  album_id: string | null;
  title_en: string;
  title_th: string | null;
  duration_seconds: number | null;
  file_storage_path: string | null;
  is_featured: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
};

const BUCKET_COVERS = 'album_covers';
const BUCKET_MUSIC = 'music_files';

export async function getAlbums() {
  const { data, error } = await supabase
    .from('albums')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data ?? []) as Album[];
}

export async function getAlbum(id: string) {
  const { data, error } = await supabase.from('albums').select('*').eq('id', id).single();
  if (error) throw error;
  return data as Album;
}

export async function getTrack(id: string) {
  const { data, error } = await supabase.from('tracks').select('*').eq('id', id).single();
  if (error) throw error;
  return data as Track;
}

export async function getTracksByAlbum(albumId: string) {
  const { data, error } = await supabase
    .from('tracks')
    .select('*')
    .eq('album_id', albumId)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data ?? []) as Track[];
}

export async function getTracksSingles() {
  const { data, error } = await supabase
    .from('tracks')
    .select('*')
    .is('album_id', null)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data ?? []) as Track[];
}

export async function createAlbum(payload: Partial<Album>) {
  const { data, error } = await supabase.from('albums').insert(payload).select('id').single();
  if (error) throw error;
  return data.id as string;
}

export async function updateAlbum(id: string, payload: Partial<Album>) {
  const { error } = await supabase.from('albums').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteAlbum(id: string) {
  const { error: tracksError } = await supabase.from('tracks').delete().eq('album_id', id);
  if (tracksError) throw tracksError;
  const { error } = await supabase.from('albums').delete().eq('id', id);
  if (error) throw error;
}

export async function createTrack(payload: Partial<Track>) {
  const { data, error } = await supabase.from('tracks').insert(payload).select('id').single();
  if (error) throw error;
  return data.id as string;
}

export async function updateTrack(id: string, payload: Partial<Track>) {
  const { error } = await supabase.from('tracks').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteTrack(id: string) {
  const { error } = await supabase.from('tracks').delete().eq('id', id);
  if (error) throw error;
}

export function getCoverPublicUrl(path: string) {
  const { data } = supabase.storage.from(BUCKET_COVERS).getPublicUrl(path);
  return data.publicUrl;
}

export function getTrackPublicUrl(path: string) {
  const { data } = supabase.storage.from(BUCKET_MUSIC).getPublicUrl(path);
  return data.publicUrl;
}

export async function uploadCover(albumSlug: string, file: File) {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const path = `${albumSlug}/cover.${ext}`;
  const { error } = await supabase.storage.from(BUCKET_COVERS).upload(path, file, { upsert: true });
  if (error) throw error;
  return path;
}

export async function uploadTrack(path: string, file: File) {
  const { error } = await supabase.storage.from(BUCKET_MUSIC).upload(path, file, { upsert: true });
  if (error) throw error;
  return path;
}
